import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import CustomFilterTabs from "../CustomFilterTabs";
import { useSnackbar } from "../SnackBar";

// Keilot's platform terms, in one window.
//
// Two modes, and the difference matters: when the terms are waiting to be
// approved this is a wall — no X, no clicking outside, and the only way past
// it is to approve or to log out. When someone opens it from the sidebar just
// to read, it is an ordinary window with an X and no checkbox.
//
// The tabs are built from whatever documents arrive, in their own order, with
// their own titles — adding a document adds a tab without touching this file.
// The documents come from GET /api/terms (loaded by CommunityLayout).
// How many there will be, and how long their titles run, is not ours to know,
// so the strip scrolls with arrows rather than assuming they fit. MUI's own
// scroll buttons measure the strip before a dialog has been laid out and then
// stay dead whatever arrives, so the arrows here are ours.

const styles = {
  paper: {
    borderRadius: "12px",
    // Small on purpose: this is something to approve and move past, not a
    // wall of legal text to be faced. The document scrolls inside it.
    width: "min(560px, 92vw)",
    height: "min(540px, 80vh)",
    maxHeight: "80vh",
    margin: "16px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "16px 20px 12px",
  },
  title: { fontSize: "18px", fontWeight: 600, color: "rgba(62, 63, 76, 1)" },
  tabsRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "0 12px",
    borderBottom: "1px solid rgba(234, 234, 234, 1)",
  },
  // MUI's own scroll buttons turn up late, beside ours — one pair is enough.
  tabsScroller: {
    flex: 1,
    minWidth: 0,
    "& .MuiTabs-scrollButtons": { display: "none" },
  },
  arrow: {
    flex: "0 0 auto",
    width: "28px",
    height: "28px",
    color: "rgba(62, 63, 76, 1)",
    "&.Mui-disabled": { opacity: 0.25 },
  },
  updated: {
    fontSize: "12px",
    color: "rgba(62, 63, 76, 0.55)",
    whiteSpace: "nowrap",
  },
  // The document itself scrolls; the frame around it never moves, so the
  // checkbox and the button stay where the eye left them.
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "12px 20px 16px",
    fontSize: "14px",
    lineHeight: "170%",
    color: "rgba(62, 63, 76, 0.9)",
    whiteSpace: "pre-wrap",
  },
  footer: {
    borderTop: "1px solid rgba(234, 234, 234, 1)",
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  checkboxRow: { display: "flex", alignItems: "center", gap: "6px", minWidth: 0 },
  checkbox: {
    padding: "4px",
    color: "rgba(62, 63, 76, 0.55)",
    "& .MuiSvgIcon-root": { fontSize: "24px" },
    "&.Mui-checked": { color: "rgba(37, 124, 109, 1)" },
  },
  checkboxLabel: { fontSize: "14px", color: "rgba(62, 63, 76, 0.9)" },
  actions: { display: "flex", alignItems: "center", gap: "12px" },
  logout: {
    fontSize: "13px",
    color: "rgba(62, 63, 76, 0.7)",
    textDecoration: "underline",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
  },
  acceptBtn: {
    backgroundColor: "rgba(37, 124, 109, 1)",
    color: "#fff",
    borderRadius: "8px",
    padding: "8px 20px",
    fontSize: "15px",
    fontWeight: 500,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { backgroundColor: "rgba(37, 124, 109, 1)", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "rgba(37, 124, 109, 0.35)", color: "#fff" },
  },
};

const NO_DOCUMENTS = [];

const PlatformTermsModal = ({
  open,
  mode = "accept",
  documents = NO_DOCUMENTS,
  updatedAt = "",
  onClose,
  onAccept,
  onLogout,
}) => {
  const { t, i18n } = useTranslation("platform-terms");
  const showSnackbar = useSnackbar();
  const language = i18n.language === "en" ? "en" : "he";
  const isRtl = language === "he";
  const [activeSlug, setActiveSlug] = useState(documents[0]?.slug);
  const [checked, setChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const stripRef = useRef(null);
  const cleanupStripRef = useRef(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);

  const mustAccept = mode === "accept";
  const active = documents.find((d) => d.slug === activeSlug) || documents[0];

  useEffect(() => {
    if (open) {
      setActiveSlug(documents[0]?.slug);
      setChecked(false);
    }
  }, [open, documents]);

  // MUI lays the tabs out inside its own scroller, so that is the element the
  // arrows move and measure.
  const scrollerOf = () =>
    stripRef.current?.querySelector(".MuiTabs-scroller") ?? null;

  // Which arrows are worth showing. RTL counts scrollLeft downwards from zero,
  // so the distance travelled is its magnitude either way.
  const measureStrip = useCallback(() => {
    const scroller = scrollerOf();
    if (!scroller) return;
    const travelled = Math.abs(scroller.scrollLeft);
    const total = scroller.scrollWidth - scroller.clientWidth;
    setCanScrollStart(travelled > 1);
    setCanScrollEnd(travelled < total - 1);
  }, []);

  // The strip arrives with the window, which the dialog mounts and remounts on
  // its own schedule, so the watching is tied to the node attaching rather
  // than to this component rendering. The row of tabs is watched as well as
  // the scroller: MUI settles the strip's styles after the opening transition,
  // and that is the moment the tabs take their real width.
  const attachStrip = useCallback(
    (node) => {
      cleanupStripRef.current?.();
      cleanupStripRef.current = null;
      stripRef.current = node;
      if (!node) return;

      const scroller = node.querySelector(".MuiTabs-scroller");
      if (!scroller) return;
      measureStrip();
      scroller.addEventListener("scroll", measureStrip);
      const observer = new ResizeObserver(measureStrip);
      observer.observe(scroller);
      if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
      cleanupStripRef.current = () => {
        scroller.removeEventListener("scroll", measureStrip);
        observer.disconnect();
      };
    },
    [measureStrip],
  );

  useEffect(() => () => cleanupStripRef.current?.(), []);

  // A tab reached with the arrows, or one that starts out of sight, is brought
  // into view rather than left for the person to hunt for.
  useEffect(() => {
    if (!open) return;
    const index = documents.findIndex((document) => document.slug === activeSlug);
    stripRef.current
      ?.querySelectorAll(".MuiTab-root")
      ?.[index]?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [activeSlug, open, documents]);

  const scrollTabs = (towardsEnd) => {
    const scroller = scrollerOf();
    if (!scroller) return;
    const step = scroller.clientWidth * 0.7;
    scroller.scrollLeft += (towardsEnd ? step : -step) * (isRtl ? -1 : 1);
    measureStrip();
  };

  if (!active) return null;

  const formatUpdated = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString(language === "en" ? "en-GB" : "he-IL");
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await onAccept?.();
    } catch (error) {
      console.error("Failed to accept the platform terms:", error);
      showSnackbar(t("accept_failed"), "error");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <Dialog
      open={open}
      // A wall, not a window: clicking outside or pressing Escape must not be
      // a way around approving.
      onClose={mustAccept ? undefined : onClose}
      disableEscapeKeyDown={mustAccept}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: styles.paper }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* One date for the whole set, opposite the title: the latest change
            to any document. */}
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{t("title")}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography sx={styles.updated}>
              {t("updated_on", { date: formatUpdated(updatedAt) })}
            </Typography>
            {!mustAccept && (
              <IconButton size="small" onClick={onClose} aria-label={t("close")}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Tabs come from the documents themselves — their titles are the
            labels, so a new document needs no change here. */}
        <Box sx={styles.tabsRow}>
          <IconButton
            size="small"
            sx={styles.arrow}
            disabled={!canScrollStart}
            onClick={() => scrollTabs(false)}
            aria-label={t("previous_tabs")}
          >
            {isRtl ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>

          <Box ref={attachStrip} sx={styles.tabsScroller}>
            <CustomFilterTabs
              scrollable
              // The labels are the documents' own titles, written by legal —
              // they render exactly as given, not title-cased by the tab style.
              tabItemStyles={{ textTransform: "none" }}
              tabs={documents.map((document) => ({
                label: document.title[language] || document.title.he,
                value: document.slug,
              }))}
              activeTab={active.slug}
              onChange={(_event, value) => setActiveSlug(value)}
            />
          </Box>

          <IconButton
            size="small"
            sx={styles.arrow}
            disabled={!canScrollEnd}
            onClick={() => scrollTabs(true)}
            aria-label={t("next_tabs")}
          >
            {isRtl ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>

        {/* Keyed on the document so switching tabs starts at the top of the new
            one, not wherever the last one was left. */}
        <Box key={active.slug} sx={styles.body}>
          {/* A document with no translation falls back to Hebrew rather than
              showing an empty tab. */}
          {active.body[language] || active.body.he}
        </Box>

        <Box sx={styles.footer}>
          {mustAccept ? (
            <>
              {/* Leaving is the way out, not the choice being offered, so it
                  sits off to the side — away from what the person came to do. */}
              <Box component="button" type="button" sx={styles.logout} onClick={onLogout}>
                {t("logout")}
              </Box>

              {/* The tick and the button are one action, so they stay together. */}
              <Box sx={styles.actions}>
                <Box sx={styles.checkboxRow}>
                  <Checkbox
                    checked={checked}
                    onChange={(event) => setChecked(event.target.checked)}
                    sx={styles.checkbox}
                    inputProps={{ "aria-label": t("checkbox") }}
                  />
                  <Typography sx={styles.checkboxLabel}>{t("checkbox")}</Typography>
                </Box>
                <Button
                  variant="contained"
                  sx={styles.acceptBtn}
                  disabled={!checked || accepting}
                  onClick={handleAccept}
                >
                  {t("accept")}
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ marginInlineStart: "auto" }}>
              <Button variant="contained" sx={styles.acceptBtn} onClick={onClose}>
                {t("close")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default PlatformTermsModal;
